interface ColumnDef {
  accessorKey?: string;
  id?: string;
}

export const formatCellValue = (column: ColumnDef, value: unknown): string => {
  const columnId = column.accessorKey;
  if (value === null || value === undefined) return '-';
  switch (columnId) {
    case 'price': {
      const numValue = typeof value === 'number' ? value : 0;
      const formatted = numValue.toFixed(2);
      const parts = formatted.split('.');
      const integerPart = parseFloat(parts[0]).toLocaleString('en-US');
      return `${integerPart}.${parts[1]}`;
    }
    case 'quantity':
    case 'settledQuantity':
    case 'pendingTransfers':
    case 'pendingSettlementQuantity':
    case 'quantityOnLoan':
    case 'adtv30D':
    case 'quantityT1':
    case 'quantityT2':
    case 'quantityTx':
    case 'l2cpQuantity':
    case 'cimbEq2Quantity':
    case 'cimbSfQuantity':
    case 'cimbSf3Quantity':
    case 'velocityLQuantity':
    case 'velocityMQuantity':
      return typeof value === 'number' ? value.toLocaleString('en-US') : '0';
    case 'l2cpAmountEur':
      return typeof value === 'number' ? `€${Math.round(value).toLocaleString('en-US')}` : '-';
    case 'l2cpRate':
    case 'l2cpGrossRate':
    case 'l2cpSnpDelta':
      return typeof value === 'number' ? value.toFixed(2) : '-';
    case 'valueEUR': {
      const numValue = typeof value === 'number' ? value : 0;
      const rounded = Math.round(numValue);
      return `€${rounded.toLocaleString('en-US')}`;
    }
    case 'dailyPotentialEUR': {
      const numValue = typeof value === 'number' ? value : 0;
      const rounded = Math.round(numValue);
      return rounded.toLocaleString('en-US');
    }
    case 'adtvAdjustedPotentialEUR': {
      const numValue = typeof value === 'number' ? value : 0;
      const rounded = Math.round(numValue);
      return rounded.toLocaleString('en-US');
    }
    case 'benchmarkFee':
    case 'activeUtilisation':
    case 'rebateRate':
      return typeof value === 'number' ? value.toFixed(2) : '0.00';
    case 'adtvMultiple':
      return typeof value === 'number' ? value.toFixed(1) : '0.0';
    case 'isLiquid':
      return value ? 'TRUE' : 'FALSE';
    case 'type':
      return String(value);
    default:
      return String(value);
  }
};

export const calculateColumnWidths = <T>(
  columns: ColumnDef[],
  data: readonly T[]
): Record<string, number> => {
  const widths: Record<string, number> = {};
  // Sample up to 200 rows for width estimation — avoids O(rows×cols) on large datasets
  const sample = data.length > 200 ? data.slice(0, 200) : data;
  columns.forEach((column) => {
    const columnId = column.accessorKey || column.id || '';
    if (columnId === 'rowNumber') {
      widths[columnId] = 40;
      return;
    }
    let maxContentWidth = 80;
    sample.forEach((row) => {
      const value = (row as Record<string, unknown>)[columnId];
      const formattedValue = formatCellValue(column, value);
      let charWidth = 7;
      if (['accountId', 'sedol', 'isin', 'symbolId', 'exchange', 'currency', 'currencyBenchmark'].includes(columnId)) {
        charWidth = 7;
      } else if (['legalEntity', 'counterparty', 'type', 'brokerAccountName', 'brokerSettlementCounterparty'].includes(columnId)) {
        charWidth = 8;
      }
      const contentWidth = formattedValue.length * charWidth + 24;
      maxContentWidth = Math.max(maxContentWidth, contentWidth);
    });
    if (columnId === 'shortName') {
      widths[columnId] = Math.min(maxContentWidth, 200);
    } else if (columnId === 'exDate') {
      widths[columnId] = 110;
    } else if (columnId === 'accountId') {
      widths[columnId] = maxContentWidth;
    } else if (columnId === 'currency') {
      widths[columnId] = Math.min(maxContentWidth + 2, 250);
    } else if (columnId === 'leverageRate') {
      widths[columnId] = Math.max(110, Math.min(maxContentWidth + 2, 250));
    } else if (columnId === 'benchmarkFee') {
      widths[columnId] = Math.max(110, Math.min(maxContentWidth + 18, 250));
    } else if (columnId === 'activeUtilisation') {
      widths[columnId] = Math.max(110, Math.min(maxContentWidth + 6, 250));
    } else if (columnId === 'currencyBenchmark') {
      widths[columnId] = Math.max(120, Math.min(maxContentWidth + 16, 250));
    } else if (columnId === 'caStatus') {
      widths[columnId] = Math.min(maxContentWidth - 20, 150);
    } else if (columnId === 'lentToCounterparties') {
      const LENT_TO_CP_HEADER_MIN = 224;
      widths[columnId] = Math.max(LENT_TO_CP_HEADER_MIN, Math.min(maxContentWidth + 80, 250));
    } else if (['l2cpQuantity', 'l2cpAmountEur', 'l2cpRate', 'l2cpGrossRate', 'l2cpSnpDelta'].includes(columnId)) {
      // Min width per column so each header fits on one line (header text + sort icon + padding)
      const L2CP_HEADER_MIN_WIDTH: Record<string, number> = {
        l2cpQuantity: 120,
        l2cpAmountEur: 190,
        l2cpRate: 105,
        l2cpGrossRate: 140,
        l2cpSnpDelta: 175,
      };
      const minW = L2CP_HEADER_MIN_WIDTH[columnId] ?? 100;
      widths[columnId] = Math.max(minW, Math.min(maxContentWidth + 16, 250));
    } else if (columnId === 'pendingTransfers') {
      widths[columnId] = Math.max(130, Math.min(maxContentWidth + 80, 250));
    } else if (columnId === 'pendingSettlementQuantity') {
      const PENDING_SETTLEMENT_HEADER_MIN = 224;
      widths[columnId] = Math.max(PENDING_SETTLEMENT_HEADER_MIN, Math.min(maxContentWidth + 80, 250));
    } else if (columnId === 'settledQuantity' || columnId === 'quantity') {
      // Both headers are wider than the figures under them, so a purely
      // content-driven width ellipsizes them away. Measured in Inter:
      // "Settled Quantity" ~106px, "(Trade Date)" ~83px.
      const HEADER_MIN = columnId === 'settledQuantity' ? 150 : 130;
      widths[columnId] = Math.max(HEADER_MIN, Math.min(maxContentWidth, 250));
    } else if (['quantityT1', 'quantityT2', 'quantityTx'].includes(columnId)) {
      widths[columnId] = Math.max(Math.min(maxContentWidth, 280), 180);
    } else if (['cimbEq2Quantity', 'cimbSfQuantity', 'cimbSf3Quantity', 'velocityLQuantity', 'velocityMQuantity'].includes(columnId)) {
      widths[columnId] = Math.max(Math.min(maxContentWidth, 140), 170);
    } else if (['brokerAccountName', 'brokerSettlementCounterparty'].includes(columnId)) {
      widths[columnId] = Math.max(160, Math.min(maxContentWidth, 250));
    } else if (['valueEUR', 'dailyPotentialEUR', 'adtvAdjustedPotentialEUR'].includes(columnId)) {
      widths[columnId] = Math.max(110, Math.min(maxContentWidth, 250));
    } else if (['rebateRate', 'adtvMultiple', 'adtv30D', 'quantityOnLoan'].includes(columnId)) {
      widths[columnId] = Math.max(100, Math.min(maxContentWidth, 250));
    } else {
      // Minimum 100px so header text + drag handle + sort icon always fit
      widths[columnId] = Math.max(100, Math.min(maxContentWidth, 250));
    }
  });
  return widths;
};

